import React from 'react';
import { ChevronLeftIcon, ClockIcon, UserIcon, InfoIcon, LinkIcon, PaperclipIcon, MessageSquareIcon, ServerIcon, ExternalLinkIcon } from 'lucide-react';
import { AuditRecord, AuditActionType } from './AuditManagement';
import { StatusBadge } from '../../StatusBadge';
interface AuditDetailProps {
  audit: AuditRecord;
  onBack: () => void;
  locale: string;
}
export function AuditDetail({
  audit,
  onBack,
  locale
}: AuditDetailProps) {
  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };
  // Render action badge
  const renderActionBadge = (action: AuditActionType) => {
    switch (action) {
      case 'Created':
        return <StatusBadge status="success" text="Created" data-id="element-16829" />;
      case 'Updated':
        return <StatusBadge status="warning" text="Updated" data-id="element-16830" />;
      case 'Deleted':
        return <StatusBadge status="error" text="Deleted" data-id="element-16831" />;
      case 'Approved':
        return <StatusBadge status="success" text="Approved" data-id="element-16832" />;
      case 'Rejected':
        return <StatusBadge status="error" text="Rejected" data-id="element-16833" />;
      case 'Viewed':
        return <StatusBadge status="default" text="Viewed" data-id="element-16834" />;
      case 'Logged In':
        return <StatusBadge status="info" text="Logged In" data-id="element-16835" />;
      case 'Logged Out':
        return <StatusBadge status="default" text="Logged Out" data-id="element-16836" />;
      case 'Password Changed':
        return <StatusBadge status="warning" text="Password Changed" data-id="element-16837" />;
      case 'Permission Changed':
        return <StatusBadge status="warning" text="Permission Changed" data-id="element-16838" />;
      case 'Configuration Changed':
        return <StatusBadge status="warning" text="Config Changed" data-id="element-16839" />;
      case 'License Activated':
        return <StatusBadge status="success" text="License Activated" data-id="element-16840" />;
      case 'License Deactivated':
        return <StatusBadge status="error" text="License Deactivated" data-id="element-16841" />;
      case 'Pass Used':
        return <StatusBadge status="info" text="Pass Used" data-id="element-16842" />;
      default:
        return <StatusBadge status="default" text={action} data-id="element-16843" />;
    }
  };
  // Determine if a value should be highlighted as added, removed, or unchanged
  const getValueClass = (oldValue: string | null, newValue: string | null) => {
    if (oldValue === null && newValue !== null) {
      return 'bg-green-50 text-green-800 px-2 py-1 rounded';
    } else if (oldValue !== null && newValue === null) {
      return 'bg-red-50 text-red-800 px-2 py-1 rounded';
    } else if (oldValue !== newValue) {
      return 'bg-yellow-50 text-yellow-800 px-2 py-1 rounded';
    }
    return '';
  };
  return <div className="space-y-6" data-id="element-16844">
      {/* Back button */}
      <div data-id="element-16845">
        <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-16846">
          <ChevronLeftIcon className="h-4 w-4 mr-2" data-id="element-16847" />
          Back to Audit Log
        </button>
      </div>
      {/* Audit details card */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-16848">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-start" data-id="element-16849">
          <div data-id="element-16850">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-16851">
              Audit Record Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-16852">
              Full information about this system action.
            </p>
          </div>
          <div className="flex items-center" data-id="element-16853">
            {renderActionBadge(audit.action)}
          </div>
        </div>
        {/* Object reference */}
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6" data-id="element-16854">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2" data-id="element-16855">
            <div className="sm:col-span-1" data-id="element-16856">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16857">
                Object Reference
              </dt>
              <dd className="mt-1 text-sm text-gray-900" data-id="element-16858">
                <div className="flex items-center" data-id="element-16859">
                  <span className="font-medium text-indigo-600 mr-2" data-id="element-16860">
                    {audit.module} #{audit.objectId}
                  </span>
                  <button className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-16861">
                    <ExternalLinkIcon className="h-3 w-3 mr-1" data-id="element-16862" />
                    View
                  </button>
                </div>
                <span className="block text-gray-500" data-id="element-16863">{audit.objectName}</span>
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-16864">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16865">Timestamp</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-16866">
                <ClockIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-16867" />
                {formatTimestamp(audit.timestamp)}
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-16868">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16869">User</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-16870">
                <UserIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-16871" />
                <div data-id="element-16872">
                  <span data-id="element-16873">{audit.userName}</span>
                  <span className="ml-2 text-xs text-gray-500" data-id="element-16874">
                    ({audit.userType})
                  </span>
                </div>
              </dd>
            </div>
            <div className="sm:col-span-1" data-id="element-16875">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16876">IP Address</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center" data-id="element-16877">
                <ServerIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-16878" />
                {audit.ipAddress || 'Not recorded'}
              </dd>
            </div>
            {(audit.entityName || audit.siteName) && <div className="sm:col-span-2" data-id="element-16879">
                <dt className="text-sm font-medium text-gray-500" data-id="element-16880">Context</dt>
                <dd className="mt-1 text-sm text-gray-900" data-id="element-16881">
                  {audit.entityName && <span className="mr-2" data-id="element-16882">Entity: {audit.entityName}</span>}
                  {audit.siteName && <span data-id="element-16883">Site: {audit.siteName}</span>}
                </dd>
              </div>}
            <div className="sm:col-span-2" data-id="element-16884">
              <dt className="text-sm font-medium text-gray-500" data-id="element-16885">Changes</dt>
              {audit.changes.length === 0 ? <dd className="mt-1 text-sm text-gray-500 italic" data-id="element-16886">
                  No field changes recorded for this action.
                </dd> : <dd className="mt-1" data-id="element-16887">
                  <div className="bg-gray-50 overflow-hidden sm:rounded-md border border-gray-200" data-id="element-16888">
                    <ul className="divide-y divide-gray-200" data-id="element-16889">
                      {audit.changes.map((change, index) => <li key={index} className="px-4 py-3" data-id="element-16890">
                          <div className="flex flex-col sm:flex-row sm:justify-between" data-id="element-16891">
                            <div className="text-sm font-medium text-gray-900 mb-1 sm:mb-0" data-id="element-16892">
                              {change.field}
                            </div>
                            <div className="flex-1 sm:ml-4 flex flex-col sm:flex-row sm:items-center text-sm" data-id="element-16893">
                              {change.oldValue !== null && <div className={`sm:text-right ${change.oldValue !== change.newValue ? 'line-through text-red-600' : ''}`} data-id="element-16894">
                                  {change.oldValue || '<empty>'}
                                </div>}
                              {change.oldValue !== null && change.newValue !== null && <div className="hidden sm:block mx-2" data-id="element-16895">→</div>}
                              {change.newValue !== null && <div className={getValueClass(change.oldValue, change.newValue)} data-id="element-16896">
                                  {change.newValue || '<empty>'}
                                </div>}
                            </div>
                          </div>
                        </li>)}
                    </ul>
                  </div>
                </dd>}
            </div>
            {audit.relatedNotes && audit.relatedNotes.length > 0 && <div className="sm:col-span-2" data-id="element-16897">
                <dt className="text-sm font-medium text-gray-500" data-id="element-16898">
                  Related Notes
                </dt>
                <dd className="mt-1" data-id="element-16899">
                  <ul className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-16900">
                    {audit.relatedNotes.map((note, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-16901">
                        <div className="w-0 flex-1 flex items-center" data-id="element-16902">
                          <MessageSquareIcon className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-16903" />
                          <span className="ml-2 flex-1 w-0 truncate" data-id="element-16904">
                            {note}
                          </span>
                        </div>
                        <div className="ml-4 flex-shrink-0" data-id="element-16905">
                          <button className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-16906">
                            View
                          </button>
                        </div>
                      </li>)}
                  </ul>
                </dd>
              </div>}
            {audit.relatedAttachments && audit.relatedAttachments.length > 0 && <div className="sm:col-span-2" data-id="element-16907">
                  <dt className="text-sm font-medium text-gray-500" data-id="element-16908">
                    Related Attachments
                  </dt>
                  <dd className="mt-1" data-id="element-16909">
                    <ul className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-16910">
                      {audit.relatedAttachments.map((attachment, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-16911">
                          <div className="w-0 flex-1 flex items-center" data-id="element-16912">
                            <PaperclipIcon className="flex-shrink-0 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-16913" />
                            <span className="ml-2 flex-1 w-0 truncate" data-id="element-16914">
                              {attachment}
                            </span>
                          </div>
                          <div className="ml-4 flex-shrink-0" data-id="element-16915">
                            <button className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-16916">
                              Download
                            </button>
                          </div>
                        </li>)}
                    </ul>
                  </dd>
                </div>}
          </dl>
        </div>
      </div>
    </div>;
}